import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import axios from 'axios';
import { ArrowLeft, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/fe0mwn3b_image.png";

export default function TeamPage() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);

  useEffect(() => {
    axios.get(`${API}/team`).then(r => setMembers(r.data)).catch(() => {});
  }, []);

  const founders = members.filter(m => m.is_founder);
  const team = members.filter(m => !m.is_founder);

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      <nav className="sticky top-0 z-50 bg-[#FDFCFB] border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="S.A.I." className="h-11" />
            <div><span className="text-base font-bold tracking-tight">S.A.I.</span>
              <span className="text-[10px] tracking-[0.2em] uppercase block text-[#5C5C5C] -mt-0.5">Design Group</span></div>
          </a>
          <Button variant="outline" onClick={() => navigate('/')} className="rounded-none border-[#121212] text-xs uppercase tracking-wider h-9">
            <ArrowLeft size={14} className="mr-2" /> Back to Home
          </Button>
        </div>
      </nav>

      <section className="py-16 px-6 border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C5C] mb-4 font-bold">The People Behind</p>
          <h1 className="text-4xl md:text-5xl tracking-tighter leading-none font-bold text-[#121212] mb-4" data-testid="team-title">
            Our Team
          </h1>
          <p className="text-base text-[#5C5C5C] max-w-xl leading-relaxed">
            Meet the creative minds and skilled professionals who bring every S.A.I. project to life.
          </p>
        </div>
      </section>

      {/* Founders */}
      {founders.length > 0 && (
        <section className="py-12 px-6 border-b border-[#E5E5E5]">
          <div className="max-w-7xl mx-auto">
            <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C5C] mb-8 font-bold">Leadership</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
              {founders.map(m => (
                <div key={m.member_id} className="group border border-[#E5E5E5] bg-white overflow-hidden" data-testid={`team-${m.member_id}`}>
                  <div className="relative h-72 overflow-hidden">
                    {(m.picture_url || m.picture_path) ? (
                      <img
                        src={m.picture_url || `${API}/team/${m.member_id}/photo`}
                        alt={m.name}
                        className="w-full h-full object-cover grayscale transition-all duration-700 ease-out group-hover:grayscale-0 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#E5E5E5] flex items-center justify-center">
                        <Users size={48} className="text-[#5C5C5C]" strokeWidth={1} />
                      </div>
                    )}
                  </div>
                  <div className="p-5 border-t border-[#E5E5E5]">
                    <p className="text-xs tracking-[0.2em] uppercase text-[#5C5C5C] mb-1">Founder</p>
                    <h3 className="text-lg font-bold text-[#121212]">{m.name}</h3>
                    <p className="text-sm text-[#5C5C5C] mt-1">{m.role}</p>
                    {m.bio && <p className="text-xs text-[#5C5C5C] mt-2 leading-relaxed">{m.bio}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Team Members */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          {team.length > 0 ? (
            <>
              <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C5C] mb-8 font-bold">Team Members</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1">
                {team.map(m => (
                  <div key={m.member_id} className="group border border-[#E5E5E5] bg-white overflow-hidden" data-testid={`team-${m.member_id}`}>
                    <div className="relative h-56 overflow-hidden">
                      {(m.picture_url || m.picture_path) ? (
                        <img
                          src={m.picture_url || `${API}/team/${m.member_id}/photo`}
                          alt={m.name}
                          className="w-full h-full object-cover grayscale transition-all duration-700 ease-out group-hover:grayscale-0 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full bg-[#E5E5E5] flex items-center justify-center">
                          <Users size={32} className="text-[#5C5C5C]" strokeWidth={1} />
                        </div>
                      )}
                    </div>
                    <div className="p-4 border-t border-[#E5E5E5]">
                      <h3 className="text-sm font-bold text-[#121212]">{m.name}</h3>
                      <p className="text-xs text-[#5C5C5C] mt-0.5">{m.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : members.length === 0 ? (
            <div className="border border-[#E5E5E5] bg-white p-16 text-center">
              <Users size={40} className="mx-auto text-[#E5E5E5] mb-4" strokeWidth={1} />
              <p className="text-sm text-[#5C5C5C]">Team information coming soon.</p>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
